import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Check, Copy } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { observer } from 'mobx-react-lite';
import { useResolve } from '@/tsyringe-hooks/useResolve';
import { ShareLinkStore, TOKEN_ShareLinkStore } from './ShareLinkStore';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AccessLevel } from '@utilitydelta/eventplanedb-client';

const ShareLinkView = observer(() => {
    const shareLinkStore = useResolve<ShareLinkStore>(TOKEN_ShareLinkStore);

    if (shareLinkStore.shareUrl) {
        return <>
            <div className="flex items-center space-x-2">
                <Input value={shareLinkStore.shareUrl} readOnly className="flex-1" />
                <Button onClick={shareLinkStore.copyShareLinkToClipboard} variant="default">
                    {shareLinkStore.shareLinkCopied ? <Check className="text-green-500" /> : <Copy />}
                </Button>
            </div>
            <div className="mt-4 flex justify-center">
                <div className="w-full sm:w-1/2 max-w-[300px">
                    <QRCodeSVG
                        value={shareLinkStore.shareUrl!}
                        size={1000}  // Set a large size that will be constrained by the container
                        style={{ width: '100%', height: 'auto' }}
                    />
                </div>
            </div>
            <div className='flex items-center justify-center'>
                <Button variant="link" onClick={shareLinkStore.generateAnotherLink}>Generate Another Link</Button>
            </div>
        </>
    }


    if (shareLinkStore.showMoreOptions) {
        return (
            <div className="w-full space-y-6">
                <div>
                    <div className="space-y-4">
                        <div className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                            <div className='space-y-1'>
                                <h3><label htmlFor="singleUse">Single Use</label></h3>
                                <p><label htmlFor="singleUse">
                                    The link can only be used once.
                                </label></p>
                            </div>
                            <Switch
                                id="singleUse"
                                checked={shareLinkStore.singleUse}
                                onCheckedChange={(checked: boolean) => {
                                    shareLinkStore.singleUse = checked;
                                }}
                            />
                        </div>
                        <div className="flex flex-col justify-between rounded-lg border p-3 shadow-sm">
                            <div className="flex flex-row items-center justify-between">
                                <div className='space-y-1 mb-1'>
                                    <h3><label htmlFor="expiresInHours">Expires In</label></h3>
                                    <p><label htmlFor="expiresInHours">
                                        Set the expiration time for the share link in hours. Leave empty for no expiration.
                                    </label></p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Input
                                    id="expiresInHours"
                                    type="number"
                                    value={shareLinkStore.expiresInHours <= 0 ? '' : shareLinkStore.expiresInHours}
                                    onChange={(e) => {
                                        const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
                                        shareLinkStore.expiresInHours = value;
                                    }}
                                    className="w-24" // Reduced width
                                />
                                <span className="text-gray-500">Hours</span> {/* Units */}
                            </div>
                        </div>
                        <div className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm space-y-1">
                            <div className='space-y-1'>
                                <h3><label htmlFor="isFork">Fork</label></h3>
                                <p><label htmlFor="isFork">Creates a copy of the project.</label></p>
                            </div>
                            <Switch
                                id="isFork"
                                checked={shareLinkStore.isFork}
                                onCheckedChange={(checked: boolean) => {
                                    shareLinkStore.isFork = checked;
                                }}
                            />
                        </div>
                        {!shareLinkStore.isFork && (
                            <div className="flex flex-col rounded-lg border p-3">
                                <div className='space-y-1 mb-1'>
                                    <h3><label htmlFor="isFork">Access Level</label></h3>
                                    <p><label htmlFor="isFork">Choose the access level for the shared link.</label></p>
                                </div>
                                <RadioGroup
                                    defaultValue={shareLinkStore.accessLevel.toString()}
                                    onValueChange={(value) => {
                                        const parsedValue = parseInt(value, 10) as AccessLevel;
                                        shareLinkStore.accessLevel = parsedValue;
                                    }}
                                    className="flex flex-col space-y-1"
                                >
                                    <div className="flex items-center space-x-2 space-y-0">
                                        <RadioGroupItem className="normalRadio" value={AccessLevel.Viewer.toString()} id="viewer" />
                                        <label htmlFor="viewer" className="font-normal">Viewer</label>
                                    </div>
                                    <div className="flex items-center space-x-2 space-y-0">
                                        <RadioGroupItem className="normalRadio" value={AccessLevel.Contributor.toString()} id="contributor" />
                                        <label htmlFor="contributor" className="font-normal">Contributor</label>
                                    </div>
                                    <div className="flex items-center space-x-2 space-y-0">
                                        <RadioGroupItem className="normalRadio" value={AccessLevel.Owner.toString()} id="owner" />
                                        <label htmlFor="owner" className="font-normal">Owner</label>
                                    </div>
                                </RadioGroup>
                            </div>
                        )}

                        {/* <div className="flex flex-col justify-between rounded-lg border p-3 shadow-sm">
                            <div className="flex flex-row items-center justify-between">
                                <div className="space-y-0.5">
                                    <div htmlFor="readOnly">Organisation Verification</div>
                                    <div>
                                        Users must enter the verification code sent to their organisation email to access the project.
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Input
                                    id="domain"
                                    placeholder="Organisation domain (e.g. example.com)"
                                    defaultValue={shareLinkStore.organisationDomain || ""}
                                    onChange={(e) => {
                                        shareLinkStore.organisationDomain = e.target.value;
                                    }}
                                />
                            </div>
                        </div> */}
                    </div>
                </div>
                <Button type="submit" onClick={(e) => {
                    e.preventDefault(); // Prevent default form submission
                    shareLinkStore.generateMoreOptionsLink(); // Call handleSubmit manually
                }} variant="default">
                    Generate Share Link
                </Button>
            </div >
        );
    }

    return <div className="flex flex-col space-y-4">
        <Button size="dynamic" variant="outlineWithDescription" onClick={shareLinkStore.viewOnlyLink}>
            <div>
                <h3>View Only</h3>
                <p className='normalText'>Users with view only access can't make changes to the project.</p>
            </div>
        </Button>
        <Button size="dynamic" variant="outlineWithDescription" onClick={shareLinkStore.contributorLink}>
            <div>
                <h3>Contributor</h3>
                <p className='normalText'>Contributors can make changes to the project. They can't give others access or delete the project.</p>
            </div>
        </Button>
        <Button size="dynamic" variant="outlineWithDescription" onClick={shareLinkStore.ownerLink}>
            <div>
                <h3>Owner</h3>
                <p className='normalText'>Owners can give other users access to your project. They can also delete the project and remove your access.</p>
            </div>
        </Button>
        <Button onClick={shareLinkStore.userRequestedMoreOptions} variant="link">More Options</Button>
    </div>

});

export default ShareLinkView;